import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import {
  TrendingUp, TrendingDown, Users, Eye, Heart, MessageCircle,
  Share2, MousePointer, DollarSign, Calendar, Hash, Award,
  BarChart3, PieChart as PieChartIcon, Activity, Target
} from 'lucide-react';
import { socialMediaAnalytics } from '../services/socialMediaAnalytics';

const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  color: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, change, icon, color }) => (
  <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
    <div className="flex items-center justify-between mb-4">
      <div className={`p-3 rounded-lg ${color}`}>
        {icon}
      </div>
      {change !== undefined && (
        <div className={`flex items-center gap-1 text-sm ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          {Math.abs(change)}%
        </div>
      )}
    </div>
    <p className="text-gray-400 text-sm mb-1">{title}</p>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

const SocialMediaAnalyticsDashboard: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(['all']);
  const [loading, setLoading] = useState(true);
  
  // State for analytics data
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalImpressions: 0,
    totalEngagement: 0,
    totalClicks: 0,
    avgEngagementRate: 0,
    impressionsChange: 0,
    engagementChange: 0,
    clicksChange: 0,
    engagementRateChange: 0
  });

  const [engagementTrends, setEngagementTrends] = useState<any[]>([]);
  const [platformComparison, setPlatformComparison] = useState<any[]>([]);
  const [topContent, setTopContent] = useState<any[]>([]);
  const [hashtagPerformance, setHashtagPerformance] = useState<any[]>([]);
  const [audienceInsights, setAudienceInsights] = useState<any>(null);
  const [campaignROI, setCampaignROI] = useState<any>(null);

  // Load analytics data
  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange, selectedPlatforms]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    
    try {
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      switch (timeRange) {
        case '7d':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(startDate.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(startDate.getDate() - 90);
          break;
      }

      // Load all analytics data
      const [
        trends,
        platforms,
        content,
        hashtags,
        audience,
        report
      ] = await Promise.all([
        socialMediaAnalytics.getEngagementTrends('day', 30),
        socialMediaAnalytics.getPlatformComparison({ start: startDate, end: endDate }),
        socialMediaAnalytics.getTopContent(10, 'engagement'),
        socialMediaAnalytics.getHashtagPerformance(),
        socialMediaAnalytics.getAudienceInsights(),
        socialMediaAnalytics.generateReport(startDate, endDate, 
          selectedPlatforms[0] === 'all' ? undefined : selectedPlatforms)
      ]);

      // Set data
      setEngagementTrends(trends);
      setPlatformComparison(platforms);
      setTopContent(content);
      setHashtagPerformance(hashtags);
      setAudienceInsights(audience);

      // Calculate summary metrics
      setSummaryMetrics({
        totalImpressions: report.summary.total_impressions,
        totalEngagement: report.summary.total_engagement,
        totalClicks: trends.reduce((sum: number, t: any) => sum + (t.clicks || 0), 0),
        avgEngagementRate: report.summary.avg_engagement_rate,
        impressionsChange: 12.5, // Mock change data
        engagementChange: 8.3,
        clicksChange: -2.1,
        engagementRateChange: 5.7
      });

      // Mock ROI data (would come from actual campaign data)
      setCampaignROI({
        roi: 2450,
        roi_percentage: 245,
        cost_per_engagement: 0.85,
        cost_per_click: 2.40,
        revenue_generated: 3450
      });

    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Ładowanie analityk...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-purple-500" />
            Analityka Social Media
          </h2>
          <p className="text-gray-400 mt-1">Szczegółowe statystyki i wyniki kampanii</p>
        </div>
        
        <div className="flex gap-3">
          {/* Time range selector */}
          <div className="flex bg-zinc-800 rounded-lg border border-zinc-700">
            {(['7d', '30d', '90d'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 text-sm font-medium transition-all ${
                  timeRange === range
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-400 hover:text-white'
                } ${range === '7d' ? 'rounded-l-lg' : range === '90d' ? 'rounded-r-lg' : ''}`}
              >
                {range === '7d' ? '7 dni' : range === '30d' ? '30 dni' : '90 dni'}
              </button>
            ))}
          </div>

          {/* Export button */}
          <button className="px-4 py-2 bg-zinc-800 text-gray-300 rounded-lg hover:bg-zinc-700 transition-all border border-zinc-700">
            📊 Eksportuj
          </button>
        </div>
      </div>

      {/* Summary metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Wyświetlenia"
          value={formatNumber(summaryMetrics.totalImpressions)}
          change={summaryMetrics.impressionsChange}
          icon={<Eye className="w-5 h-5 text-white" />}
          color="bg-blue-600"
        />
        <MetricCard
          title="Zaangażowanie"
          value={formatNumber(summaryMetrics.totalEngagement)}
          change={summaryMetrics.engagementChange}
          icon={<Heart className="w-5 h-5 text-white" />}
          color="bg-purple-600"
        />
        <MetricCard
          title="Kliknięcia"
          value={formatNumber(summaryMetrics.totalClicks)}
          change={summaryMetrics.clicksChange}
          icon={<MousePointer className="w-5 h-5 text-white" />}
          color="bg-green-600"
        />
        <MetricCard
          title="Współczynnik zaangażowania"
          value={`${summaryMetrics.avgEngagementRate.toFixed(2)}%`}
          change={summaryMetrics.engagementRateChange}
          icon={<Activity className="w-5 h-5 text-white" />}
          color="bg-orange-600"
        />
      </div>

      {/* Main charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Engagement trends */}
        <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-500" />
            Trend zaangażowania
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={engagementTrends}>
              <defs>
                <linearGradient id="colorEngagement" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorImpressions" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
                labelStyle={{ color: '#f3f4f6' }}
              />
              <Area 
                type="monotone" 
                dataKey="engagement" 
                stroke="#8b5cf6" 
                fillOpacity={1} 
                fill="url(#colorEngagement)" 
              />
              <Area 
                type="monotone" 
                dataKey="impressions" 
                stroke="#3b82f6" 
                fillOpacity={1} 
                fill="url(#colorImpressions)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Platform comparison */}
        <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <PieChartIcon className="w-5 h-5 text-blue-500" />
            Porównanie platform
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={platformComparison}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="engagement"
              >
                {platformComparison.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Platform details */}
      <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-green-500" />
          Szczegóły platform
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={platformComparison}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="platform" stroke="#9ca3af" />
            <YAxis stroke="#9ca3af" />
            <Tooltip 
              contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
              labelStyle={{ color: '#f3f4f6' }}
            />
            <Legend />
            <Bar dataKey="impressions" fill="#3b82f6" name="Wyświetlenia" />
            <Bar dataKey="engagement" fill="#8b5cf6" name="Zaangażowanie" />
            <Bar dataKey="engagement_rate" fill="#10b981" name="Współczynnik %" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Bottom sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top content */}
        <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-yellow-500" />
            Najlepsze posty
          </h3>
          <div className="space-y-3">
            {topContent.slice(0, 5).map((post, index) => (
              <div key={post.post_id} className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  index === 0 ? 'bg-yellow-600' : index === 1 ? 'bg-gray-500' : index === 2 ? 'bg-orange-700' : 'bg-zinc-700'
                }`}>
                  {index + 1}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-white truncate">{post.content}</p>
                  <p className="text-xs text-gray-400">
                    {formatNumber(post.metric_value)} zaangażowań
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Hashtag performance */}
        <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Hash className="w-5 h-5 text-blue-500" />
            Najlepsze hashtagi
          </h3>
          <div className="space-y-3">
            {hashtagPerformance.slice(0, 5).map((tag) => (
              <div key={tag.hashtag} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-blue-400">#</span>
                  <span className="text-white">{tag.hashtag}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm text-white">{formatNumber(tag.avg_engagement)}</p>
                  <p className="text-xs text-gray-400">{tag.usage_count} użyć</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ROI metrics */}
        {campaignROI && (
          <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              ROI kampanii
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-400 mb-1">Zwrot z inwestycji</p>
                <p className="text-2xl font-bold text-green-400">
                  {campaignROI.roi_percentage.toFixed(1)}%
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-400">Koszt/zaangażowanie</p>
                  <p className="text-white font-medium">{campaignROI.cost_per_engagement.toFixed(2)} zł</p>
                </div>
                <div>
                  <p className="text-gray-400">Koszt/kliknięcie</p>
                  <p className="text-white font-medium">{campaignROI.cost_per_click.toFixed(2)} zł</p>
                </div>
              </div>
              <div className="pt-3 border-t border-zinc-700">
                <p className="text-gray-400 text-sm">Przychód wygenerowany</p>
                <p className="text-xl font-bold text-white">
                  {campaignROI.revenue_generated.toLocaleString('pl-PL')} zł
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Audience insights */}
      {audienceInsights && (
        <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-500" />
            Analiza odbiorców
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Age distribution */}
            <div>
              <h4 className="text-sm font-medium text-gray-400 mb-3">Wiek</h4>
              <div className="space-y-2">
                {Object.entries(audienceInsights.demographics.age_groups).map(([age, percent]) => (
                  <div key={age} className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">{age}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-zinc-700 rounded-full h-2">
                        <div 
                          className="bg-purple-500 h-2 rounded-full" 
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-400 w-10 text-right">{percent}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Gender distribution */}
            <div>
              <h4 className="text-sm font-medium text-gray-400 mb-3">Płeć</h4>
              <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                  <Pie
                    data={Object.entries(audienceInsights.demographics.gender).map(([gender, value]) => ({
                      name: gender === 'male' ? 'Mężczyźni' : gender === 'female' ? 'Kobiety' : 'Inne',
                      value
                    }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={50}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {Object.entries(audienceInsights.demographics.gender).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Top locations */}
            <div>
              <h4 className="text-sm font-medium text-gray-400 mb-3">Lokalizacje</h4>
              <div className="space-y-2">
                {Object.entries(audienceInsights.demographics.locations)
                  .slice(0, 5)
                  .map(([location, percent]) => (
                    <div key={location} className="flex items-center justify-between">
                      <span className="text-sm text-gray-300">{location}</span>
                      <span className="text-sm text-white font-medium">{percent}%</span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Peak times */}
            <div>
              <h4 className="text-sm font-medium text-gray-400 mb-3">Najlepsze godziny</h4>
              <ResponsiveContainer width="100%" height={150}>
                <LineChart data={audienceInsights.peak_times}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="hour" stroke="#9ca3af" />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="engagement" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    dot={{ fill: '#10b981' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialMediaAnalyticsDashboard;
